import { TypedDocumentNode, gql } from '@apollo/client';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { TeamLogoWithSeasonOverlay_team } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  isolation: isolate;

  --team-logo-size: var(--quadruple-unit);
  --season-font-size: 10px;

  @media ${tabletAndAbove} {
    --team-logo-size: calc(6 * var(--unit));
    --season-font-size: var(--intermediate-unit);
  }

  width: var(--team-logo-size);

  &.isCollectionLink:hover {
    transform: scale(1.06);
  }
  .isCollectionLink:hover & {
    transform: scale(1.06);
  }
`;

const Season = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  font: var(--t-label-xs);
  line-height: 1;
  font-size: var(--season-font-size);

  white-space: nowrap;
  border: 1px solid var(--c-nd-200);
  background: var(--c-nd-100);
  border-radius: 0.4em;
  padding: 0.1em 0.2em;
  transform: translate(-50%, 25%);
`;

type Props = {
  team: TeamLogoWithSeasonOverlay_team;
  season: number | string;
  to?: To;
  onClick?: () => void;
};

export const TeamLogoWithSeasonOverlay = ({
  team,
  season,
  to,
  onClick,
}: Props) => {
  const seasonFormat = `${String(Number(season))}`;
  const linkProps = to
    ? { as: Link, to, className: 'isCollectionLink', onClick }
    : {};
  return (
    <Wrapper {...linkProps}>
      <TeamAvatar team={team} />
      <Season>{seasonFormat}</Season>
    </Wrapper>
  );
};

TeamLogoWithSeasonOverlay.fragments = {
  team: gql`
    fragment TeamLogoWithSeasonOverlay_team on Team {
      ... on TeamInterface {
        slug
      }
      ...TeamAvatar_team
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<TeamLogoWithSeasonOverlay_team>,
};
